/**
 * Reading-plan progress — LOCAL-FIRST access to `bible_reading_progress`.
 *
 * Read: reactive PowerSync query with ALL active user rows
 * (tiny dataset: <= day count of started plans). Write: marking a
 * day complete inserts a row (or restores tombstone); unchecking applies
 * soft-delete (`deleted_at`), SAME tombstone contract as `bible_bookmark`
 * — server write-path stamps `user_id` and verifies row ownership.
 */
import {
  buildReadingProgressRow,
  queryKeys,
  restoreReadingProgressMutation,
  softDeleteReadingProgressMutation,
} from "@bso/shared";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { bible_reading_progress, eq } from "@bso/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import type { PlanProgressRow } from "../logic";

interface ProgressRow extends PlanProgressRow {
  id: string;
  deleted_at: string | null;
}

export function useReadingProgress() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  // ALL user rows (including tombstones, to restore them
  // on re-checking a day); active view is filtered in memory.
  const progressQuery = createPowerSyncQuery<ProgressRow>({
    queryKey: () => queryKeys.readingPlans.progress(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const u = session.userId();
      if (!dbInstance || !u) {
        return null;
      }
      return dbInstance
        .select({
          id: bible_reading_progress.id,
          plan_id: bible_reading_progress.plan_id,
          day: bible_reading_progress.day,
          deleted_at: bible_reading_progress.deleted_at,
        })
        .from(bible_reading_progress)
        .where(eq(bible_reading_progress.user_id, u));
    },
    enabled: () => Boolean(db()) && Boolean(drizzle()) && Boolean(session.userId()),
    suspense: false,
  });

  /** ACTIVE rows (no tombstone) — input to `computePlanProgress`. */
  const activeRows = (): PlanProgressRow[] =>
    (progressQuery.data ?? []).filter((row) => row.deleted_at === null);

  const rowsOfDay = (planId: string, day: number): ProgressRow[] =>
    (progressQuery.data ?? []).filter((row) => row.plan_id === planId && row.day === day);

  /** Marks day as read (idempotent; restores tombstone if existed). */
  const completeDay = async (planId: string, day: number) => {
    const dbInstance = drizzle();
    const u = session.userId();
    if (!dbInstance || !u) {
      return;
    }
    const rows = rowsOfDay(planId, day);
    try {
      if (rows.some((row) => row.deleted_at === null)) {
        return; // already marked
      }
      const tombstone = rows[0];
      if (tombstone) {
        await restoreReadingProgressMutation(dbInstance, tombstone.id);
        return;
      }
      await dbInstance
        .insert(bible_reading_progress)
        .values(buildReadingProgressRow(u, planId, day));
    } catch (e) {
      console.error("Failed to complete plan day:", e);
    }
  };

  /**
   * Unchecks day (logical tombstone for sync). Tombstones ALL
   * active rows for that day (two devices may mark offline).
   */
  const uncompleteDay = async (planId: string, day: number) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    const actives = (progressQuery.data ?? []).filter(
      (row) => row.plan_id === planId && row.day === day && row.deleted_at === null,
    );
    try {
      await softDeleteReadingProgressMutation(
        dbInstance,
        actives.map((row) => row.id),
      );
    } catch (e) {
      console.error("Failed to uncomplete plan day:", e);
    }
  };

  const ready = () => Boolean(drizzle()) && Boolean(session.userId());

  return { activeRows, completeDay, uncompleteDay, ready };
}
